import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import * as api from './service/studyService'
import { surveyModalSize } from './content/surveyResolver'
import StudyLogin from './StudyLogin'
import AdminBar from './AdminBar'
import StudyNav from './StudyNav'
import Modal from './components/Modal'
import ChiBackdrop from './components/ChiBackdrop'
import InstructionStep from './steps/InstructionStep'
import ConsentStep from './steps/ConsentStep'
import RecordingCheckStep from './steps/RecordingCheckStep'
import ObjectivesStep from './steps/ObjectivesStep'
import WaitingStep from './steps/WaitingStep'
import SurveyStep from './steps/SurveyStep'
import TaskStep from './steps/TaskStep'
import QuizStep from './steps/QuizStep'
import BookingGateStep from './steps/BookingGateStep'
import DoneStep from './steps/DoneStep'
import './study.scss'

const PID_KEY = 'study_participant_id'

// One URL per part of the study. The SERVER still decides which part a participant is
// actually in — these paths follow the phase on the descriptor, they don't select it.
// Opening /study/main during part 1 lands you on /study/screening, not part 2.
export const PHASE_PATH = {
  screening: '/study/screening',
  main: '/study/main',
  followup: '/study/followup'
}

// Canonical URL for a step: coding tasks stay on the server-gated /chi/{n}; everything
// else is the phase path. `mode` is only the fallback for when there's no descriptor yet
// (first paint, or an error), so a failed load doesn't bounce off its own URL.
function canonicalPath (step, mode) {
  if (step && step.route === 'chi' && step.questionNumber) return `/chi/${step.questionNumber}`
  if (step && step.phase) return PHASE_PATH[step.phase] || '/study'
  return PHASE_PATH[mode] || '/study'
}

// mode: 'auto' | 'screening' | 'main' | 'followup'. 'auto' is bare /study — it loads,
// then redirects to whichever part the participant is really in.
export default function StudyPage ({ mode = 'auto' }) {
  const [pid, setPid] = useState(() => localStorage.getItem(PID_KEY) || '')
  const [config, setConfig] = useState({ testMode: false, testParticipants: [] })
  const [step, setStep] = useState(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => { api.getConfig().then(setConfig).catch(() => {}) }, [])

  const load = useCallback(async () => {
    if (!pid) return
    setError('')
    try {
      const d = mode === 'followup' ? await api.getFollowup(pid) : await api.getCurrent(pid)
      setStep(d)
      // Server-gated redirect: if the URL doesn't match the participant's actual step, fix it.
      const canonical = canonicalPath(d, mode)
      if (location.pathname !== canonical) navigate(canonical, { replace: true })
    } catch (e) {
      setError(e.response?.data?.error || e.message)
    }
  }, [pid, mode, location.pathname, navigate])

  useEffect(() => { load() }, [load])

  const onLogin = (id) => { localStorage.setItem(PID_KEY, id); setPid(id) }
  const onLogout = () => { localStorage.removeItem(PID_KEY); setPid(''); setStep(null); navigate('/study') }

  const onAdvance = async (payload, reason = 'complete') => {
    setBusy(true)
    try {
      const next = await api.advance(pid, payload, reason)
      setStep(next)
      const canonical = canonicalPath(next, mode)
      if (location.pathname !== canonical) navigate(canonical, { replace: true })
    } catch (e) {
      setError(e.response?.data?.error || e.message)
    } finally { setBusy(false) }
  }

  // No participant id in storage. The JWT lives in localStorage too, so clearing
  // storage clears both — such a participant is unauthenticated anyway and simply
  // needs their invite link again, which restores everything. The type-in form
  // stays for test mode and for anyone the researcher is walking through by hand.
  if (!pid) {
    return (
      <div className="study-root study-login">
        <div className="study-card">
          <h1 className="study-h1">VizPI Study</h1>
          <p>Please open the personal study link from your invitation email to continue.</p>
        </div>
        {config?.testMode && <StudyLogin config={config} onLogin={onLogin} />}
      </div>
    )
  }

  const adminBar = config.testMode
    ? <AdminBar pid={pid} config={config} onSwitch={onLogin} onLogout={onLogout} onRefresh={load} />
    : null

  const common = { step, participantId: pid, onAdvance, busy }

  // Modal content for non-coding steps.
  const modalStep = () => {
    if (!step) return { size: 'md', node: <div className="study-card">Loading…</div> }
    if (error) return { size: 'md', node: <div className="study-card study-error">Cannot load: <code>{error}</code></div> }
    // Parked between part 1 and part 2 while the warm-up is scored.
    if (step.waiting) return { size: 'md', node: <WaitingStep excluded={step.excluded} /> }
    if (step.done || step.type === 'done') return { size: 'md', node: <DoneStep step={step} phase={step.phase} testMode={config.testMode} /> }
    switch (step.type) {
      case 'consent': return { size: 'lg', node: <ConsentStep {...common} /> }
      case 'recording_check': return { size: 'lg', node: <RecordingCheckStep {...common} /> }
      case 'objectives': return { size: 'xl', node: <ObjectivesStep {...common} /> }
      case 'instructions': return { size: 'lg', node: <InstructionStep {...common} /> }
      case 'survey': return {
        size: surveyModalSize(step.survey, !!step.intro),
        node: <SurveyStep {...common} />
      }
      case 'quiz': return { size: 'lg', node: <QuizStep {...common} /> }
      case 'booking_gate': return { size: 'md', node: <BookingGateStep {...common} /> }
      default: return { size: 'md', node: <div className="study-card">Unknown step: {step.type}</div> }
    }
  }

  const isChi = step && step.route === 'chi' && !error

  return (
    <div className="study-root">
      {adminBar}
      {isChi
        ? <div className="study-fullbleed"><TaskStep {...common} /></div>
        : (() => {
            const m = modalStep()
            return (
              <>
                <ChiBackdrop />
                <Modal size={m.size}>{m.node}</Modal>
              </>
            )
          })()}
      {/* TEMPORARY back/next — outside the modal so it sits at the bottom of the
          window on every screen, task and modal alike, rather than inside whichever
          card happens to be showing. Remove before participants run. */}
      {step && !error && <StudyNav pid={pid} step={step} onRefresh={load} />}
    </div>
  )
}
